import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authService, tareasService, cursosService } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import HtmlContent from '../components/HtmlContent';
import UploadProgress from '../components/UploadProgress';
import { TIPOS_ARCHIVO_ENTREGA } from '../constants/tareaArchivoTipos';
import {
  TAREA_MATERIAL_MAX_ARCHIVOS,
  TAREA_MATERIAL_MAX_ARCHIVO_BYTES,
  TAREA_MATERIAL_MAX_ARCHIVO_MB,
} from '../constants/uploadLimits';
import ConfirmPopup from '../components/ConfirmPopup';
import './Tareas.css';

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const buildTareaFormData = (submitData, archivosNuevos, archivosEliminar) => {
  const fd = new FormData();
  fd.append('titulo', submitData.titulo || '');
  fd.append('descripcion', submitData.descripcion || '');
  fd.append('fecha_cierre', submitData.fecha_cierre || '');
  fd.append('se_califica', submitData.se_califica ? 'true' : 'false');
  fd.append('es_obligatorio', submitData.es_obligatorio ? 'true' : 'false');
  fd.append('aceptar_vencidos', submitData.aceptar_vencidos ? 'true' : 'false');
  fd.append('entrega_en_grupos', submitData.entrega_en_grupos ? 'true' : 'false');
  if (submitData.curso != null) fd.append('curso', String(submitData.curso));
  if (submitData.num_grupos != null) fd.append('num_grupos', String(submitData.num_grupos));
  if (submitData.max_integrantes_por_grupo != null) {
    fd.append('max_integrantes_por_grupo', String(submitData.max_integrantes_por_grupo));
  }
  if (submitData.reutilizar_grupos_de != null && submitData.reutilizar_grupos_de !== '') {
    fd.append('reutilizar_grupos_de', String(submitData.reutilizar_grupos_de));
  }
  fd.append('tipos_entrega', JSON.stringify(submitData.tipos_entrega || []));
  fd.append('tipos_archivo_entrega', JSON.stringify(submitData.tipos_archivo_entrega || []));
  if (submitData.extensiones_script != null) {
    fd.append('extensiones_script', submitData.extensiones_script);
  }
  if (Array.isArray(submitData.archivos_reutilizar) && submitData.archivos_reutilizar.length > 0) {
    fd.append('archivos_reutilizar', JSON.stringify(submitData.archivos_reutilizar));
  }
  archivosNuevos.forEach((file) => fd.append('archivos', file));
  if (archivosEliminar.length > 0) {
    fd.append('archivos_eliminar', JSON.stringify(archivosEliminar));
  }
  return fd;
};

const getTipoEntregaLabel = (tipo) => {
  const labels = { archivo: '📎 Archivo', enlace: '🔗 Enlace', texto: '📝 Texto', codigo: '💻 Código' };
  return labels[tipo] || tipo;
};

const TareaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'nuevo');
  const materialInputRef = useRef(null);

  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    fecha_cierre: '',
    se_califica: false,
    es_obligatorio: true,
    aceptar_vencidos: false,
    entrega_en_grupos: false,
    reutilizar_grupos_de: null,
    num_grupos: '',
    max_integrantes_por_grupo: '',
    tipos_entrega: [],
    tipos_archivo_entrega: [],
    extensiones_script: '.py, .js, .html, .css',
    curso: null,
  });
  const [cursos, setCursos] = useState([]);
  const [todasTareas, setTodasTareas] = useState([]);
  const [archivosExistentes, setArchivosExistentes] = useState([]);
  const [archivosNuevos, setArchivosNuevos] = useState([]);
  const [archivosEliminar, setArchivosEliminar] = useState([]);
  const [archivosReutilizarIds, setArchivosReutilizarIds] = useState([]);
  const [reutilizables, setReutilizables] = useState([]);
  const [reutilizarCodigo, setReutilizarCodigo] = useState(null);
  const [reutilizarId, setReutilizarId] = useState('');
  const [loadingReutilizar, setLoadingReutilizar] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [isProfesor, setIsProfesor] = useState(false);
  const [isCoordinador, setIsCoordinador] = useState(false);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    const savedRole = localStorage.getItem('selectedRole');
    setIsProfesor(savedRole === 'Profesor');
    setIsCoordinador(savedRole === 'Coordinador');
  }, [navigate]);

  useEffect(() => {
    if (currentUser === null) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await cursosService.getCursos();
        // Solo cursos activos
        let list = (data || []).filter(c => c.activo !== false);
        // Si no es coordinador, solo cursos donde el usuario está en el equipo (profesor, tutor, etc.)
        if (isProfesor && !isCoordinador && currentUser) {
          list = list.filter(curso => {
            if (curso.equipo && Array.isArray(curso.equipo)) {
              return curso.equipo.some(m => {
                const uid = typeof m === 'object' && m && (m.id != null || m.user_id != null) ? (m.user_id ?? m.id) : m;
                return parseInt(uid) === parseInt(currentUser.id);
              });
            }
            return false;
          });
        }
        setCursos(list);
        const tareasList = await tareasService.getTareas();
        setTodasTareas(Array.isArray(tareasList) ? tareasList : []);
        if (isEdit) {
          const tarea = await tareasService.getTarea(id);
          setFormData({
            titulo: tarea.titulo || '',
            descripcion: tarea.descripcion || '',
            fecha_cierre: tarea.fecha_cierre ? String(tarea.fecha_cierre).slice(0, 10) : '',
            se_califica: tarea.se_califica || false,
            es_obligatorio: tarea.es_obligatorio !== undefined ? tarea.es_obligatorio : true,
            aceptar_vencidos: tarea.aceptar_vencidos === true,
            entrega_en_grupos: tarea.entrega_en_grupos === true,
            reutilizar_grupos_de: tarea.reutilizar_grupos_de || null,
            num_grupos: tarea.num_grupos != null ? String(tarea.num_grupos) : '',
            max_integrantes_por_grupo: tarea.max_integrantes_por_grupo != null ? String(tarea.max_integrantes_por_grupo) : '',
            tipos_entrega: tarea.tipos_entrega || [],
            tipos_archivo_entrega: tarea.tipos_archivo_entrega || [],
            extensiones_script: tarea.extensiones_script || '.py, .js, .html, .css',
            curso: tarea.curso || null,
          });
          setArchivosExistentes(tarea.archivos_material || []);
          setArchivosNuevos([]);
          setArchivosEliminar([]);
          setArchivosReutilizarIds([]);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Error al cargar');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, id, isEdit, isProfesor, isCoordinador]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'entrega_en_grupos' && type === 'checkbox' && !checked) {
      setFormData(prev => ({
        ...prev,
        entrega_en_grupos: false,
        reutilizar_grupos_de: null,
        num_grupos: '',
        max_integrantes_por_grupo: '',
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleReutilizarGruposChange = (e) => {
    const val = e.target.value;
    if (!val) {
      setFormData(prev => ({ ...prev, reutilizar_grupos_de: null }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      reutilizar_grupos_de: parseInt(val, 10),
      num_grupos: '',
      max_integrantes_por_grupo: '',
    }));
  };

  const tareasOrigenGrupos = useMemo(() => {
    if (!formData.curso || !formData.entrega_en_grupos) return [];
    return todasTareas.filter((t) => (
      t.entrega_en_grupos
      && String(t.curso) === String(formData.curso)
      && !t.reutilizar_grupos_de
      && (!isEdit || String(t.id) !== String(id))
    ));
  }, [todasTareas, formData.curso, formData.entrega_en_grupos, isEdit, id]);

  const usaGruposCompartidos = Boolean(formData.reutilizar_grupos_de);

  const handleTiposEntregaChange = (tipo) => {
    setFormData(prev => {
      const tipos = prev.tipos_entrega || [];
      if (tipos.includes(tipo)) {
        const next = tipos.filter((t) => t !== tipo);
        return {
          ...prev,
          tipos_entrega: next,
          tipos_archivo_entrega: tipo === 'archivo' ? [] : prev.tipos_archivo_entrega,
        };
      }
      return { ...prev, tipos_entrega: [...tipos, tipo] };
    });
  };

  const handleTiposArchivoChange = (categoriaId) => {
    setFormData((prev) => {
      const list = prev.tipos_archivo_entrega || [];
      if (list.includes(categoriaId)) {
        return { ...prev, tipos_archivo_entrega: list.filter((id) => id !== categoriaId) };
      }
      return { ...prev, tipos_archivo_entrega: [...list, categoriaId] };
    });
  };

  const handleCursoChange = (e) => {
    const cursoId = e.target.value;
    setFormData(prev => ({
      ...prev,
      curso: cursoId ? parseInt(cursoId, 10) : null,
      reutilizar_grupos_de: null,
    }));
    setReutilizarId('');
  };

  useEffect(() => {
    if (!formData.curso) {
      setReutilizables([]);
      setReutilizarCodigo(null);
      return;
    }
    const curso = cursos.find((c) => String(c.id) === String(formData.curso));
    if (!curso?.codigo) {
      setReutilizables([]);
      setReutilizarCodigo(null);
      return;
    }
    let cancelled = false;
    setLoadingReutilizar(true);
    tareasService
      .getTareasReutilizables(formData.curso, isEdit ? id : null)
      .then((data) => {
        if (cancelled) return;
        setReutilizarCodigo(data?.codigo || curso.codigo || null);
        setReutilizables(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) {
          setReutilizables([]);
          setReutilizarCodigo(curso.codigo || null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingReutilizar(false);
      });
    return () => { cancelled = true; };
  }, [formData.curso, cursos, isEdit, id]);

  const aplicarTareaReutilizable = () => {
    const item = reutilizables.find((t) => String(t.id) === String(reutilizarId));
    if (!item) {
      setError('Seleccioná una tarea de la carpeta de material para reutilizar.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      titulo: item.titulo || prev.titulo,
      descripcion: item.descripcion || '',
      se_califica: item.se_califica === true,
      es_obligatorio: item.es_obligatorio !== false,
      aceptar_vencidos: item.aceptar_vencidos === true,
      entrega_en_grupos: item.entrega_en_grupos === true,
      tipos_entrega: Array.isArray(item.tipos_entrega) ? item.tipos_entrega : [],
      tipos_archivo_entrega: Array.isArray(item.tipos_archivo_entrega) ? item.tipos_archivo_entrega : [],
      extensiones_script: item.extensiones_script || prev.extensiones_script || '.py, .js, .html, .css',
      // No copiar fecha_cierre / grupos de otra edición
      reutilizar_grupos_de: null,
      num_grupos: '',
      max_integrantes_por_grupo: '',
    }));
    const mats = Array.isArray(item.archivos_material) ? item.archivos_material : [];
    setArchivosEliminar((prev) => {
      const propios = archivosExistentes.filter((a) => !a._reutilizado).map((a) => a.id);
      return [...new Set([...prev, ...propios])];
    });
    setArchivosReutilizarIds(mats.map((a) => a.id).filter(Boolean));
    setArchivosExistentes(mats.map((a) => ({
      id: a.id,
      nombre_original: a.nombre_original || 'Archivo',
      url: a.url,
      _reutilizado: true,
    })));
    setArchivosNuevos([]);
    setError('');
  };

  const totalArchivosMaterial = () => {
    const existentesActivos = archivosExistentes.filter((a) => !archivosEliminar.includes(a.id)).length;
    return existentesActivos + archivosNuevos.length;
  };

  const handleMaterialFilesChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;

    const demasiadoGrandes = picked.filter((f) => f.size > TAREA_MATERIAL_MAX_ARCHIVO_BYTES);
    if (demasiadoGrandes.length > 0) {
      setError(`Cada archivo no puede superar ${TAREA_MATERIAL_MAX_ARCHIVO_MB} MB.`);
      return;
    }

    const espacio = TAREA_MATERIAL_MAX_ARCHIVOS - totalArchivosMaterial();
    if (espacio <= 0) {
      setError(`Como máximo ${TAREA_MATERIAL_MAX_ARCHIVOS} archivos de material por tarea.`);
      return;
    }

    const aAgregar = picked.slice(0, espacio);
    setArchivosNuevos((prev) => [...prev, ...aAgregar]);
    if (picked.length > espacio) {
      setError(`Solo se agregaron ${espacio} archivo(s). El límite es ${TAREA_MATERIAL_MAX_ARCHIVOS}.`);
    } else {
      setError('');
    }
  };

  const quitarArchivoNuevo = (index) => {
    setArchivosNuevos((prev) => prev.filter((_, i) => i !== index));
  };

  const quitarArchivoExistente = (archivoId) => {
    setArchivosEliminar((prev) => (prev.includes(archivoId) ? prev : [...prev, archivoId]));
    setArchivosReutilizarIds((prev) => prev.filter((id) => id !== archivoId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    setUploadProgress(0);
    const progressOpts = { onUploadProgress: (percent) => setUploadProgress(percent) };
    try {
      if (!formData.curso) {
        setError('Debe seleccionar un curso');
        return;
      }
      if (!formData.tipos_entrega || formData.tipos_entrega.length === 0) {
        setError('Debe seleccionar al menos un tipo de entrega');
        return;
      }
      if (
        formData.tipos_entrega.includes('archivo') &&
        (!formData.tipos_archivo_entrega || formData.tipos_archivo_entrega.length === 0)
      ) {
        setError('Si permite entrega por archivo, debe seleccionar al menos un tipo de archivo.');
        return;
      }
      const submitData = {
        ...formData,
        curso: formData.curso || null,
        tipos_archivo_entrega: formData.tipos_entrega.includes('archivo')
          ? formData.tipos_archivo_entrega
          : [],
        extensiones_script: formData.tipos_entrega.includes('archivo') && (formData.tipos_archivo_entrega || []).includes('script')
          ? formData.extensiones_script
          : '',
        num_grupos: formData.reutilizar_grupos_de
          ? null
          : (formData.entrega_en_grupos && formData.num_grupos !== '' ? parseInt(formData.num_grupos, 10) : null),
        max_integrantes_por_grupo: formData.reutilizar_grupos_de
          ? null
          : (formData.entrega_en_grupos && formData.max_integrantes_por_grupo !== '' ? parseInt(formData.max_integrantes_por_grupo, 10) : null),
        reutilizar_grupos_de: formData.entrega_en_grupos && formData.reutilizar_grupos_de
          ? formData.reutilizar_grupos_de
          : null,
        archivos_reutilizar: archivosReutilizarIds.filter((aid) => !archivosEliminar.includes(aid)),
      };
      if (totalArchivosMaterial() > TAREA_MATERIAL_MAX_ARCHIVOS) {
        setError(`Como máximo ${TAREA_MATERIAL_MAX_ARCHIVOS} archivos de material por tarea.`);
        return;
      }
      const usarMultipart = archivosNuevos.length > 0;
      if (usarMultipart) {
        const formDataToSend = buildTareaFormData(submitData, archivosNuevos, archivosEliminar);
        if (isEdit) {
          await tareasService.updateTarea(id, formDataToSend, progressOpts);
        } else {
          await tareasService.createTarea(formDataToSend, progressOpts);
        }
      } else if (isEdit && archivosEliminar.length > 0) {
        await tareasService.updateTarea(id, { ...submitData, archivos_eliminar: archivosEliminar });
      } else if (isEdit) {
        await tareasService.updateTarea(id, submitData);
      } else {
        await tareasService.createTarea(submitData);
      }
      setUploadProgress(100);
      navigate('/dashboard/tareas', { state: formData.curso != null ? { selectedCursoId: String(formData.curso) } : {} });
    } catch (err) {
      const errorData = err.response?.data || {};
      let errorMessage = errorData.error || 'Error al guardar la tarea';
      if (typeof errorData === 'object' && !errorData.error) {
        const errors = [];
        Object.keys(errorData).forEach(key => {
          if (Array.isArray(errorData[key]) && errorData[key].length > 0) errors.push(`${key}: ${errorData[key][0]}`);
          else if (typeof errorData[key] === 'string') errors.push(`${key}: ${errorData[key]}`);
        });
        if (errors.length > 0) errorMessage = errors.join(' | ');
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const archivosMaterialPreview = [
    ...archivosExistentes.filter((a) => !archivosEliminar.includes(a.id)).map((a) => ({
      key: `ex-${a.id}`,
      nombre: a.nombre_original || 'Archivo',
    })),
    ...archivosNuevos.map((f, i) => ({
      key: `new-${f.name}-${i}`,
      nombre: f.name,
    })),
  ];

  if (loading) {
    return (
      <div className="tareas-container">
        <div className="tareas-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
      </div>
    );
  }

  return (
    <div className="tareas-container">
      <div className="tareas-form-page-header">
        <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => navigate('/dashboard/tareas')}>
          ← Volver
        </button>
        <h1>{isEdit ? 'Editar Tarea' : 'Nueva Tarea'}</h1>
      </div>
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      {submitting && uploadProgress > 0 && uploadProgress < 100 && (
        <UploadProgress percent={uploadProgress} label="Subiendo archivos..." />
      )}
      <div className="tareas-form-with-preview">
      <form onSubmit={handleSubmit} className="tareas-form tareas-form-page">
        <div className="tareas-form-group">
          <label htmlFor="titulo">Título *</label>
          <input type="text" id="titulo" name="titulo" value={formData.titulo} onChange={handleInputChange} required maxLength={200} />
        </div>
        <div className="tareas-form-group">
          <label htmlFor="descripcion">Descripción</label>
          <RichTextEditor
            value={formData.descripcion}
            onChange={(html) => setFormData(prev => ({ ...prev, descripcion: html }))}
            placeholder="Descripción de la tarea..."
            minHeight="140px"
            allowImageUpload
          />
        </div>
        <div className="tareas-form-group tareas-form-material">
          <label>Material para la actividad (opcional)</label>
          <p className="tareas-form-hint">
            Puede adjuntar archivos para que los estudiantes los descarguen al realizar la tarea.
            Máximo {TAREA_MATERIAL_MAX_ARCHIVOS} archivos de hasta {TAREA_MATERIAL_MAX_ARCHIVO_MB} MB cada uno.
          </p>
          <input
            ref={materialInputRef}
            type="file"
            multiple
            className="tareas-form-file-input-hidden"
            onChange={handleMaterialFilesChange}
          />
          <button
            type="button"
            className="tareas-btn tareas-btn-secondary tareas-btn-add-file"
            onClick={() => materialInputRef.current?.click()}
            disabled={submitting || totalArchivosMaterial() >= TAREA_MATERIAL_MAX_ARCHIVOS}
          >
            📎 Agregar archivo(s)
          </button>
          {(archivosExistentes.some((a) => !archivosEliminar.includes(a.id)) || archivosNuevos.length > 0) && (
            <ul className="entrega-archivos-list tareas-material-archivos-list">
              {archivosExistentes
                .filter((a) => !archivosEliminar.includes(a.id))
                .map((a) => (
                  <li key={`ex-${a.id}`}>
                    <span title={a.nombre_original}>{a.nombre_original || 'Archivo'}</span>
                    <button
                      type="button"
                      className="tareas-btn-remove-file"
                      onClick={() => quitarArchivoExistente(a.id)}
                      title="Quitar"
                      disabled={submitting}
                    >
                      ×
                    </button>
                  </li>
                ))}
              {archivosNuevos.map((f, i) => (
                <li key={`new-${f.name}-${f.size}-${i}`}>
                  <span title={f.name}>
                    {f.name}
                    <span className="tareas-material-archivo-size"> ({formatBytes(f.size)})</span>
                  </span>
                  <button
                    type="button"
                    className="tareas-btn-remove-file"
                    onClick={() => quitarArchivoNuevo(i)}
                    title="Quitar"
                    disabled={submitting}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="tareas-form-group">
          <label htmlFor="fecha_cierre">Fecha de Cierre *</label>
          <input type="date" id="fecha_cierre" name="fecha_cierre" value={formData.fecha_cierre} onChange={handleInputChange} required />
        </div>
        <div className="tareas-form-row">
          <div className="tareas-form-group">
            <label className="tareas-checkbox-label">
              <input type="checkbox" name="se_califica" checked={formData.se_califica} onChange={handleInputChange} />
              <span>Se califica</span>
            </label>
          </div>
          <div className="tareas-form-group">
            <label className="tareas-checkbox-label">
              <input type="checkbox" name="es_obligatorio" checked={formData.es_obligatorio} onChange={handleInputChange} />
              <span>Es obligatorio</span>
            </label>
          </div>
          <div className="tareas-form-group">
            <label className="tareas-checkbox-label">
              <input type="checkbox" name="aceptar_vencidos" checked={formData.aceptar_vencidos} onChange={handleInputChange} />
              <span>Aceptar vencidos</span>
            </label>
          </div>
          <div className="tareas-form-group">
            <label className="tareas-checkbox-label">
              <input type="checkbox" name="entrega_en_grupos" checked={formData.entrega_en_grupos} onChange={handleInputChange} />
              <span>Entrega en grupos</span>
            </label>
          </div>
        </div>
        {formData.entrega_en_grupos && (
          <>
            <div className="tareas-form-group">
              <label htmlFor="reutilizar_grupos_de">Grupos de la tarea</label>
              <p className="tareas-form-hint">
                Podés crear grupos nuevos o reutilizar los de otra actividad del mismo curso (mismos integrantes en todas las partes).
              </p>
              <select
                id="reutilizar_grupos_de"
                name="reutilizar_grupos_de"
                className="tareas-form-select"
                value={formData.reutilizar_grupos_de || ''}
                onChange={handleReutilizarGruposChange}
              >
                <option value="">Crear grupos nuevos para esta tarea</option>
                {tareasOrigenGrupos.map((t) => (
                  <option key={t.id} value={t.id}>{t.titulo}</option>
                ))}
              </select>
              {tareasOrigenGrupos.length === 0 && (
                <p className="tareas-form-hint">No hay otras tareas con grupos propios en este curso.</p>
              )}
            </div>
            {!usaGruposCompartidos && (
              <div className="tareas-form-row">
                <div className="tareas-form-group">
                  <label htmlFor="num_grupos">Cantidad de grupos</label>
                  <input type="number" id="num_grupos" name="num_grupos" min="0" placeholder="Ej: 5" value={formData.num_grupos} onChange={handleInputChange} />
                </div>
                <div className="tareas-form-group">
                  <label htmlFor="max_integrantes_por_grupo">Máx. integrantes por grupo</label>
                  <input type="number" id="max_integrantes_por_grupo" name="max_integrantes_por_grupo" min="1" placeholder="Ej: 4" value={formData.max_integrantes_por_grupo} onChange={handleInputChange} />
                </div>
              </div>
            )}
            {usaGruposCompartidos && (
              <p className="tareas-form-hint tareas-form-hint--info">
                Los integrantes se gestionan en la tarea origen. Configuralos en Gestión de grupos.
              </p>
            )}
          </>
        )}
        <div className="tareas-form-group">
          <label>Tipos de Entrega *</label>
          <div className="tareas-checkbox-group">
            {['archivo', 'enlace', 'texto', 'codigo'].map(tipo => (
              <label key={tipo} className="tareas-checkbox-label">
                <input type="checkbox" checked={formData.tipos_entrega.includes(tipo)} onChange={() => handleTiposEntregaChange(tipo)} />
                <span>{getTipoEntregaLabel(tipo)}</span>
              </label>
            ))}
          </div>
        </div>
        {formData.tipos_entrega.includes('archivo') && (
          <div className="tareas-form-group tareas-form-tipos-archivo">
            <label>Tipos de archivo permitidos *</label>
            <p className="tareas-form-hint">El estudiante solo podrá subir archivos con estas extensiones.</p>
            <div className="tareas-checkbox-group">
              {TIPOS_ARCHIVO_ENTREGA.map(({ id, label, extensiones }) => (
                <div key={id} className="tareas-form-tipo-archivo-item">
                  <label className="tareas-checkbox-label tareas-checkbox-label--block">
                    <input
                      type="checkbox"
                      checked={(formData.tipos_archivo_entrega || []).includes(id)}
                      onChange={() => handleTiposArchivoChange(id)}
                    />
                    <span>
                      {label}{' '}
                      {id !== 'script' && (
                        <span className="tareas-form-ext-hint">({extensiones.map((e) => `.${e}`).join(', ')})</span>
                      )}
                    </span>
                  </label>
                  {id === 'script' && (formData.tipos_archivo_entrega || []).includes('script') && (
                    <div className="tareas-form-script-exts-wrap">
                      <label htmlFor="extensiones_script" className="tareas-form-script-exts-label">
                        Extensiones permitidas:
                      </label>
                      <input
                        type="text"
                        id="extensiones_script"
                        name="extensiones_script"
                        value={formData.extensiones_script}
                        onChange={(e) => setFormData((prev) => ({ ...prev, extensiones_script: e.target.value }))}
                        placeholder=".py, .js, .html, .css"
                        className="tareas-form-script-exts-input"
                      />
                      <small className="tareas-form-script-exts-help">
                        Separadas por coma (ej: <code>.py, .js, .html, .css, .ts</code>)
                      </small>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="tareas-form-group">
          <label htmlFor="curso">Curso *</label>
          {isEdit ? (
            <p className="tareas-form-curso-readonly">
              {cursos.find(c => c.id === formData.curso)?.nombre || '—'}
            </p>
          ) : (
            <select id="curso" name="curso" value={formData.curso || ''} onChange={handleCursoChange} required>
              <option value="">Seleccionar curso</option>
              {cursos.map(curso => (
                <option key={curso.id} value={curso.id}>
                  {curso.nombre}{curso.codigo ? ` (${curso.codigo})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        {formData.curso && (
          <div className="tareas-form-group tareas-form-reutilizar">
            <label htmlFor="reutilizar_tarea">Carpeta de material (mismo código)</label>
            <p className="tareas-form-hint">
              {reutilizarCodigo
                ? `Reutilizá título, descripción, tipos de entrega y archivos de otras ediciones con código “${reutilizarCodigo}”.`
                : 'Este curso no tiene código. Para compartir material entre ediciones, asignales el mismo código al crear/editar el curso.'}
            </p>
            {reutilizarCodigo && (
              <div className="tareas-form-reutilizar-row">
                <select
                  id="reutilizar_tarea"
                  className="tareas-form-select"
                  value={reutilizarId}
                  onChange={(e) => setReutilizarId(e.target.value)}
                  disabled={loadingReutilizar || reutilizables.length === 0}
                >
                  <option value="">
                    {loadingReutilizar
                      ? 'Cargando…'
                      : (reutilizables.length === 0 ? 'No hay tareas previas' : 'Elegir tarea a reutilizar…')}
                  </option>
                  {reutilizables.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.titulo} — {t.curso_nombre}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="tareas-btn tareas-btn-secondary"
                  onClick={aplicarTareaReutilizable}
                  disabled={!reutilizarId || submitting}
                >
                  Aplicar contenido
                </button>
              </div>
            )}
          </div>
        )}
        <div className="tareas-modal-actions">
          <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => navigate('/dashboard/tareas', { state: formData.curso != null ? { selectedCursoId: String(formData.curso) } : {} })} disabled={submitting}>Cancelar</button>
          <button type="submit" className="tareas-btn tareas-btn-primary" disabled={submitting}>{isEdit ? 'Actualizar' : 'Crear'}</button>
        </div>
      </form>
      <aside className="tareas-form-preview">
        <h3 className="tareas-form-preview-title">Vista previa</h3>
        <div className="tareas-form-preview-content">
          <h2 className="tareas-preview-titulo">{formData.titulo || 'Título de la tarea'}</h2>
          {formData.descripcion ? (
            <div className="tareas-preview-desc"><HtmlContent content={formData.descripcion} tag="div" /></div>
          ) : (
            <p className="tareas-preview-sin-desc">Sin descripción</p>
          )}
          {archivosMaterialPreview.length > 0 && (
            <div className="tareas-preview-material">
              <strong>Material adjunto:</strong>
              <ul className="tareas-preview-material-list">
                {archivosMaterialPreview.map((a) => (
                  <li key={a.key}>📎 {a.nombre}</li>
                ))}
              </ul>
            </div>
          )}
          {formData.fecha_cierre && (
            <p className="tareas-preview-fecha"><strong>Fecha de cierre:</strong> {new Date(formData.fecha_cierre + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
          )}
          <div className="tareas-preview-opciones">
            {formData.se_califica && <span className="tareas-preview-badge">Se califica</span>}
            {formData.es_obligatorio && <span className="tareas-preview-badge">Obligatorio</span>}
            {formData.aceptar_vencidos && <span className="tareas-preview-badge">Acepta vencidos</span>}
          </div>
          {formData.tipos_entrega && formData.tipos_entrega.length > 0 && (
            <div className="tareas-preview-tipos">
              <strong>Tipos de entrega:</strong>
              <div className="tareas-preview-tipos-list">
                {formData.tipos_entrega.map(tipo => (
                  <span key={tipo} className="tareas-preview-tipo-badge">{getTipoEntregaLabel(tipo)}</span>
                ))}
              </div>
              {formData.tipos_entrega.includes('archivo') && (formData.tipos_archivo_entrega || []).length > 0 && (
                <p className="tareas-preview-archivo-tipos">
                  <strong>Archivos:</strong>{' '}
                  {TIPOS_ARCHIVO_ENTREGA.filter((t) => formData.tipos_archivo_entrega.includes(t.id))
                    .map((t) => {
                      if (t.id === 'script') {
                        const custom = (formData.extensiones_script || '')
                          .split(',')
                          .map((e) => e.trim().replace(/^\./, ''))
                          .filter(Boolean)
                          .map((e) => `.${e}`)
                          .join(', ');
                        return `Script (${custom || '.py, .js, .html, .css'})`;
                      }
                      return t.label;
                    })
                    .join(', ')}
                </p>
              )}
            </div>
          )}
        </div>
      </aside>
      </div>
    </div>
  );
};

export default TareaForm;
